import React, { useEffect, useState, useContext, useRef, useCallback } from 'react'
import ModalTest from '../../../BaseModal'
import { CloseDiv, ContainerOfModal, FormContainer, Header, HeaderTitle, InputDiv, Label, Input, ButtonContainer, Button, CheckboxContainer, HiddenCheckbox, StyledCheckbox } from '../../../styles/modal-style'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { X } from 'lucide-react'
import ConfirmationModal from '../../../ModalConfirmation'
import SelectSupplierInResource from '../../Resource/CreateResource/SelectSupplier'
import SelectMachineInService from './SelectMachine'
import InfiniteSelect from '../../../InfiniteSelect'
import PriceManager, { PriceItem, PriceManagerRef } from '../../../PriceManager'
import CostManager, { CostItem, CostManagerRef } from '../../../CostManager'
import ApiService from '../../../../services/ApiService'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import * as S from '../../../styles/modal-style'
import TimeField from '../../../TimeField'

interface CompanyItemType {
  id: number
  companyName: string
  cnpj: string
  contactName?: string
}

interface MachineItemType {
  id: number
  name: string
  model: string
}

interface CategoryItemType {
  id: number
  name: string
}

interface TypeItemType {
  id: number
  name: string
}

interface AreaItemType {
  id: number
  name: string
}

interface SectorItemType {
  id: number
  name: string
}

interface ServiceFormType {
  name: string
  code: string
  category: string
  type: string
  prices: PriceItem[]
  costs: CostItem[]
  deadlineInDays: string
  description: string
  observation: string
  executionTime: number | null
  setupTime: number | null
  markup: string
  allowZeroCost: boolean
}

const CreateServiceModal = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')

  const { setFeedback } = useContext(FeedbackContext)
  const [nameError, setNameError] = useState(false)
  const [areaError, setAreaError] = useState(false)
  const [sectorError, setSectorError] = useState(false)
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false)
  const [validationFields, setValidationFields] = useState<string[]>([])
  const priceManagerRef = useRef<PriceManagerRef>(null)
  const currentPricesRef = useRef<PriceItem[]>([])
  const costManagerRef = useRef<CostManagerRef>(null)
  const currentCostsRef = useRef<CostItem[]>([])
  const [selectedSuppliers, setSelectedSuppliers] = useState<CompanyItemType[]>([])
  const [selectedMachines, setSelectedMachines] = useState<MachineItemType[]>([])
  const [selectedCategories, setSelectedCategories] = useState<CategoryItemType[]>([])
  const [selectedTypes, setSelectedTypes] = useState<TypeItemType[]>([])
  const [selectedAreas, setSelectedAreas] = useState<AreaItemType[]>([])
  const [selectedSectors, setSelectedSectors] = useState<SectorItemType[]>([])
  const [previousCategoryId, setPreviousCategoryId] = useState<number | null>(null)
  const [previousAreaId, setPreviousAreaId] = useState<number | null>(null)
  const [formData, setFormData] = useState<ServiceFormType>({
    name: '',
    code: '',
    category: '',
    type: '',
    prices: [],
    costs: [],
    deadlineInDays: '',
    description: '',
    observation: '',
    executionTime: null,
    setupTime: null,
    markup: '',
    allowZeroCost: false,
  })

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        try {
          const response = await ApiService.services.findById(Number(id))
          const data = response.data
          const {
            suppliers,
            machines,
            category,
            type,
            sector,
            Category_Stage,
            Type_Category_Stage,
            prices,
            costs,
            ...rest
          } = data
          setFormData({
            ...rest,
            id: undefined,
            prices: prices || [],
            costs: costs || [],
            executionTime: rest.executionTime ?? null,
            setupTime: rest.setupTime ?? null,
            markup: rest.markup !== null && rest.markup !== undefined ? String(rest.markup) : '',
            allowZeroCost: rest.allowZeroCost === true,
          })
          currentPricesRef.current = prices || []
          currentCostsRef.current = costs || []
          setSelectedSuppliers(suppliers)
          setSelectedMachines(machines)

          const resolvedCategory = category || Category_Stage
          const resolvedType = type || Type_Category_Stage

          if (resolvedCategory) {
            setSelectedCategories([resolvedCategory])
          }
          if (resolvedType) {
            setSelectedTypes([resolvedType])
          }
          if (sector) {
            setSelectedSectors([sector])
            if (sector.Area) {
              setSelectedAreas([sector.Area])
              setPreviousAreaId(sector.Area.id)
            }
          }
        } catch (error) {
          console.log(error)
        }
      }
    }
    fetchData()
  }, [id])

  useEffect(() => {
    const currentCategoryId = selectedCategories[0]?.id || null

    if (selectedCategories.length === 0) {
      setSelectedTypes([])
      setPreviousCategoryId(null)
    } else if (currentCategoryId !== previousCategoryId && previousCategoryId !== null) {
      setSelectedTypes([])
      setPreviousCategoryId(currentCategoryId)
    } else if (previousCategoryId === null) {
      setPreviousCategoryId(currentCategoryId)
    }
  }, [selectedCategories, previousCategoryId])

  useEffect(() => {
    const currentAreaId = selectedAreas[0]?.id || null

    if (selectedAreas.length === 0) {
      setSelectedSectors([])
      setPreviousAreaId(null)
    } else if (currentAreaId !== previousAreaId && previousAreaId !== null) {
      setSelectedSectors([])
      setPreviousAreaId(currentAreaId)
    } else if (previousAreaId === null) {
      setPreviousAreaId(currentAreaId)
    }
  }, [selectedAreas, previousAreaId])

  const handleInputChange = (
    field: keyof ServiceFormType,
    value: string | number | boolean | null | PriceItem[] | CostItem[],
  ) => {
    if (field === 'prices' && Array.isArray(value)) {
      currentPricesRef.current = value as PriceItem[];
    }
    if (field === 'costs' && Array.isArray(value)) {
      currentCostsRef.current = value as CostItem[];
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    let hasError = false
    const missingFields: string[] = []
    if (!formData.name.trim()) {
      setNameError(true)
      missingFields.push('Nome')
      hasError = true
    }
    if (!selectedAreas[0]?.id) {
      setAreaError(true)
      missingFields.push('Área')
      hasError = true
    }
    if (!selectedSectors[0]?.id) {
      setSectorError(true)
      missingFields.push('Setor')
      hasError = true
    }

    if (hasError) {
      setValidationFields(missingFields)
      setIsValidationModalOpen(true)
      return
    }

    if (priceManagerRef.current) {
      const priceAdded = priceManagerRef.current.addPendingPrice();

      if (priceAdded) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    if (costManagerRef.current) {
      const costAdded = costManagerRef.current.addPendingCost();

      if (costAdded) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    await new Promise(resolve => setTimeout(resolve, 50));

    try {
      const finalPrices = currentPricesRef.current.length > 0 ? currentPricesRef.current : formData.prices;
      const finalCosts = currentCostsRef.current.length > 0 ? currentCostsRef.current : formData.costs;

      const { category, type, ...cleanFormData } = formData as any
      const selectedCategory = selectedCategories[0] || null
      const selectedType = selectedTypes[0] || null
      const selectedSector = selectedSectors[0] || null

      const requestData = {
        ...cleanFormData,
        prices: finalPrices.map((price: any) => ({
          price: Number(price.price),
          unitOfMeasurementId: Number(price.unitOfMeasurementId)
        })),
        costs: finalCosts.map((cost: any) => ({
          cost: Number(cost.cost),
          unitOfMeasurementId: Number(cost.unitOfMeasurementId)
        })),
        deadlineInDays: formData.deadlineInDays ? Number(formData.deadlineInDays) : null,
        supplierIds: selectedSuppliers.map(supplier => supplier.id),
        machineIds: selectedMachines.map(machine => machine.id),
        categoryId: selectedCategory ? selectedCategory.id : null,
        category: selectedCategory ? selectedCategory.name : null,
        typeId: selectedType ? selectedType.id : null,
        type: selectedType ? selectedType.name : null,
        sectorId: selectedSector ? selectedSector.id : null,
        executionTime: formData.executionTime ?? null,
        setupTime: formData.setupTime ?? null,
        markup: formData.markup ? Number(formData.markup) : null,
        allowZeroCost: formData.allowZeroCost,
      }

      const action = id ? 'atualizar' : 'criar'

      if (id) {
        await ApiService.services.update(Number(id), requestData)
      } else {
        await ApiService.services.create(requestData)
      }

      setFeedback({
        message: `Serviço ${action} com sucesso!`,
        isOpen: true,
        statusOk: true
      })

      navigate('..')
    } catch (error: any) {
      const action = id ? 'atualizar' : 'criar'
      let errorMessage = `Erro ao ${action} serviço`

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.status === 400) {
        errorMessage = 'Dados inválidos. Verifique as informações inseridas.'
      } else if (error.response?.status === 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente.'
      } else if (error.message) {
        errorMessage = error.message
      }

      setFeedback({
        message: errorMessage,
        isOpen: true,
        statusOk: false
      })
    }
  }

  return (
    <ModalTest>
      <ConfirmationModal
        isOpen={isValidationModalOpen}
        title="Campos obrigatórios não preenchidos"
        message={
          <div>
            Preencha os seguintes campos antes de continuar:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {validationFields.map((field, i) => (
                <li key={i}>{field}</li>
              ))}
            </ul>
          </div>
        }
        actions={[{ label: 'Entendi', onClick: () => setIsValidationModalOpen(false), variant: 'primary' }]}
      />
      <ContainerOfModal width='850px'>
        <Header>
          <HeaderTitle>{id ? 'Editar serviço' : 'Novo serviço'}</HeaderTitle>
          <CloseDiv onClick={() => navigate('..')}> <X size={20} /></CloseDiv>
        </Header>
        <FormContainer onSubmit={onSubmit} columns='1fr 1fr'>
          <InputDiv>
            <Label style={nameError ? { color: 'red' } : {}}>
              Nome
              <span style={{ color: 'red', marginLeft: 2 }}>*</span>
              {nameError && (
                <span style={{ fontSize: '12px', color: 'red', marginLeft: 6, fontWeight: 400 }}>
                  O nome é obrigatório!
                </span>
              )}
            </Label>
            <Input
              autoComplete='off'
              type='text'
              value={formData.name}
              onChange={(e) => {
                handleInputChange('name', e.target.value)
                setNameError(false)
              }}
              placeholder='Digite aqui'
              style={{ height: '40px', ...(nameError ? { border: '1px solid red', borderRadius: '2rem' } : {}) }}
            />
          </InputDiv>

          <InputDiv>
            <Label style={areaError ? { color: 'red' } : {}}>
              Área
              <span style={{ color: 'red', marginLeft: 2 }}>*</span>
              {areaError && (
                <span style={{ fontSize: '12px', color: 'red', marginLeft: 6, fontWeight: 400 }}>
                  A área é obrigatória!
                </span>
              )}
            </Label>
            <div style={areaError ? { border: '1px solid red', borderRadius: '2rem', overflow: 'hidden' } : {}}>
              <InfiniteSelect
                mode="crud"
                value={selectedAreas[0] || null}
                onSelect={(option) => {
                  setSelectedAreas(option ? [option] : [])
                  setAreaError(false)
                }}
                fetchFunction={async (limit: number, offset: number, search?: string) => {
                  const response = await ApiService.services.getAreas({ limit, offset, query: search })
                  return response.data
                }}
                onItemCreate={async (name: string) => {
                  const response = await ApiService.services.createArea({ name })
                  const newArea = response.data
                  setSelectedAreas([newArea])
                  setAreaError(false)
                  return newArea
                }}
                onItemDelete={async (id: any, cascade?: boolean) => {
                  try {
                    await ApiService.services.deleteArea(id, cascade)
                  } catch (error: any) {
                    console.error("Erro ao deletar área:", error)
                    const backendMsg = error?.response?.data?.message || error?.message || 'Falha ao deletar a área.'
                    throw new Error(Array.isArray(backendMsg) ? backendMsg[0] : backendMsg)
                  }
                }}
                placeholder="Digite para buscar ou criar área"
                initialOptions={selectedAreas}
                allowCreate={true}
                allowDelete={true}
                allowCascadeDelete={true}
                cascadeDeleteLabel="Excluir a área e todos os setores vinculados a ela"
                showCreateOnlyIfNotExists={true}
                minCreateLength={2}
              />
            </div>
          </InputDiv>

          <InputDiv>
            <Label style={sectorError ? { color: 'red' } : {}}>
              Setor
              <span style={{ color: 'red', marginLeft: 2 }}>*</span>
              {sectorError && (
                <span style={{ fontSize: '12px', color: 'red', marginLeft: 6, fontWeight: 400 }}>
                  O setor é obrigatório!
                </span>
              )}
            </Label>
            <div style={sectorError ? { border: '1px solid red', borderRadius: '2rem', overflow: 'hidden' } : {}}>
              <InfiniteSelect
                mode="crud"
                value={selectedSectors[0] || null}
                onSelect={(option) => {
                  setSelectedSectors(option ? [option] : [])
                  setSectorError(false)
                }}
                fetchFunction={useCallback(async (limit: number, offset: number, search?: string) => {
                  if (!selectedAreas[0]?.id) return []
                  const response = await ApiService.services.getSectors({ limit, offset, query: search, areaId: selectedAreas[0]?.id })
                  return response.data
                }, [selectedAreas])}
                onItemCreate={async (name: string) => {
                  if (!selectedAreas[0]?.id) {
                    throw new Error("Selecione uma área primeiro")
                  }
                  try {
                    const response = await ApiService.services.createSector({
                      name,
                      areaId: selectedAreas[0].id
                    })
                    const newSector = response.data
                    setSelectedSectors([newSector])
                    setSectorError(false)
                    return newSector
                  } catch (err: any) {
                    const existingList = await ApiService.services.getSectors({ areaId: selectedAreas[0].id, query: name })
                    const matched = (existingList.data || []).find((s: any) => (s.name || '').toLowerCase().trim() === name.toLowerCase().trim()) || existingList.data?.[0]
                    if (matched) {
                      setSelectedSectors([matched])
                      setSectorError(false)
                      return matched
                    }
                    throw err
                  }
                }}
                onItemDelete={async (id: any) => {
                  try {
                    await ApiService.services.deleteSector(id)
                  } catch (error: any) {
                    console.error("Erro ao deletar setor:", error)
                    const backendMsg = error?.response?.data?.message || error?.message || 'Falha ao deletar o setor.'
                    throw new Error(Array.isArray(backendMsg) ? backendMsg[0] : backendMsg)
                  }
                }}
                placeholder={selectedAreas.length > 0 ? "Digite para buscar ou criar setor" : "Primeiro selecione uma área"}
                disabled={selectedAreas.length === 0}
                initialOptions={selectedSectors}
              allowCreate={true}
              allowDelete={true}
              showCreateOnlyIfNotExists={true}
              minCreateLength={2}
            />
          </div>
        </InputDiv>

          <InputDiv>
            <Label>Categoria</Label>
            <InfiniteSelect
              mode="crud"
              value={selectedCategories[0] || null}
              onSelect={(option) => setSelectedCategories(option ? [option] : [])}
              fetchFunction={async (limit: number, offset: number, search?: string) => {
                const response = await ApiService.services.getCategories({
                  params: { limit, offset, query: search }
                });
                return response.data;
              }}
              onItemCreate={async (name: string) => {
                try {
                  const response = await ApiService.services.createCategory({ name });
                  const newCategory = response.data;
                  setSelectedCategories([newCategory]);
                  return newCategory;
                } catch (error) {
                  console.error("Erro ao criar nova categoria:", error);
                  throw error;
                }
              }}
              onItemDelete={async (id: any, cascade?: boolean) => {
                try {
                  await ApiService.services.deleteCategory(id, cascade);
                } catch (error: any) {
                  console.error("Erro ao deletar categoria:", error);
                  const backendMsg = error?.response?.data?.message || error?.message || 'Falha ao deletar a categoria.';
                  throw new Error(Array.isArray(backendMsg) ? backendMsg[0] : backendMsg);
                }
              }}
              placeholder="Digite para buscar ou criar categoria"
              initialOptions={selectedCategories}
              allowCreate={true}
              allowDelete={true}
              allowCascadeDelete={true}
              cascadeDeleteLabel="Excluir a categoria e todos os tipos vinculados a ela"
              showCreateOnlyIfNotExists={true}
              minCreateLength={2}
            />
          </InputDiv>

          <InputDiv>
            <Label>Tipo</Label>
            <InfiniteSelect
              key={`service-type-${selectedCategories[0]?.id || 'none'}`}
              mode="crud"
              value={selectedTypes[0] || null}
              onSelect={(option) => setSelectedTypes(option ? [option] : [])}
              categoryId={selectedCategories[0]?.id}
              fetchFunction={useCallback(async (limit: number, offset: number, search?: string) => {
                if (!selectedCategories[0]?.id) return [];
                const response = await ApiService.services.getTypes({
                  limit,
                  offset,
                  query: search,
                  categoryId: selectedCategories[0]?.id
                });
                return response.data;
              }, [selectedCategories])}
              onItemCreate={async (name: string) => {
                if (!selectedCategories[0]?.id) {
                  console.error("Selecione uma categoria primeiro");
                  throw new Error("Selecione uma categoria primeiro");
                }
                try {
                  const response = await ApiService.services.createType({
                    name,
                    categoryId: selectedCategories[0].id
                  });
                  const newType = response.data;
                  setSelectedTypes([newType]);
                  return newType;
                } catch (error) {
                  console.error("Erro ao criar novo tipo:", error);
                  throw error;
                }
              }}
              onItemDelete={async (id: any) => {
                try {
                  await ApiService.services.deleteType(id);
                } catch (error: any) {
                  console.error("Erro ao deletar tipo:", error);
                  const backendMsg = error?.response?.data?.message || error?.message || 'Falha ao deletar o tipo.';
                  throw new Error(Array.isArray(backendMsg) ? backendMsg[0] : backendMsg);
                }
              }}
              placeholder={selectedCategories.length > 0 ? "Digite para buscar ou criar tipo" : "Primeiro selecione uma categoria"}
              disabled={selectedCategories.length === 0}
              initialOptions={selectedTypes}
              allowCreate={true}
              allowDelete={true}
              showCreateOnlyIfNotExists={true}
              minCreateLength={2}
            />
          </InputDiv>

          <InputDiv>
            <Label>Código</Label>
            <Input
              autoComplete='off'
              type='text'
              value={formData.code}
              onChange={(e) => handleInputChange('code', e.target.value)}
              placeholder='Digite aqui'
              style={{ height: '40px' }}
            />
          </InputDiv>

          <SelectSupplierInResource suppliers={selectedSuppliers} setSuppliers={setSelectedSuppliers} />

          <SelectMachineInService machines={selectedMachines} setMachines={setSelectedMachines} />

          <PriceManager
            ref={priceManagerRef}
            prices={formData.prices}
            onChange={(prices) => handleInputChange('prices', prices)}
            baseUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/services` || ''}
            label="Preços"
          />

          <CostManager
            ref={costManagerRef}
            costs={formData.costs}
            onChange={(costs) => handleInputChange('costs', costs)}
            label="Custos"
          />

          <InputDiv>
            <Label>Markup</Label>
            <Input
              autoComplete='off'
              type='number'
              step='0.01'
              value={formData.markup}
              onChange={(e) => handleInputChange('markup', e.target.value)}
              placeholder='Ex: 1.5'
              style={{ height: '40px' }}
            />
          </InputDiv>

          <InputDiv>
            <Label>Custo zero</Label>
            <CheckboxContainer>
              <HiddenCheckbox
                id='allowZeroCost'
                checked={formData.allowZeroCost}
                onChange={(e) => handleInputChange('allowZeroCost', e.target.checked)}
              />
              <StyledCheckbox
                checked={formData.allowZeroCost}
                onClick={() => handleInputChange('allowZeroCost', !formData.allowZeroCost)}
              />
              <Label htmlFor='allowZeroCost' style={{ marginBottom: 0, marginLeft: '10px', cursor: 'pointer' }}>
                Pode ser vendido com custo zero
              </Label>
            </CheckboxContainer>
          </InputDiv>

          <InputDiv>
            <Label>Prazo em Dias</Label>
            <Input
              autoComplete='off'
              type='number'
              value={formData.deadlineInDays}
              onChange={(e) => handleInputChange('deadlineInDays', e.target.value)}
              placeholder='Digite aqui'
              style={{ height: '40px' }}
            />
          </InputDiv>
          <InputDiv>
            <Label>Tempo de execução</Label>
            <TimeField
              valueSeconds={formData.executionTime}
              onChangeSeconds={(seconds) => handleInputChange('executionTime', seconds)}
            />
          </InputDiv>
          <InputDiv>
            <Label>Tempo de setup</Label>
            <TimeField
              valueSeconds={formData.setupTime}
              onChangeSeconds={(seconds) => handleInputChange('setupTime', seconds)}
            />
          </InputDiv>
          <S.InputDiv>
            <S.Label>Descrição detalhada</S.Label>
            <S.Textarea placeholder='Digite aqui' name='description' value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} />
          </S.InputDiv>
          <S.InputDiv>
            <S.Label>Observações</S.Label>
            <S.Textarea placeholder='Digite aqui' name='observation' value={formData.observation} onChange={(e) => handleInputChange('observation', e.target.value)} />
          </S.InputDiv>
          <ButtonContainer>
            <Button
              type='button'
              onClick={() => navigate('..')}
              className='cancel'
            >
              Cancelar
            </Button>
            <Button type='submit'>
              {id ? 'Alterar' : '+ Adicionar'} serviço
            </Button>
          </ButtonContainer>
        </FormContainer>
      </ContainerOfModal>
    </ModalTest>
  )
}

export default CreateServiceModal
